export const SHOWCASE_CAPTION_STATUSES = [
    "none",
    "queued",
    "running",
    "review",
    "failed",
];
export const SHOWCASE_CAPTION_LANGS = ["sv", "en"];
export const SHOWCASE_CAPTION_LABELS = {
    sv: "Svenska",
    en: "English",
};
export const SHOWCASE_CAPTION_LOCALES = {
    sv: "sv-SE",
    en: "en-US",
};
export function isCaptionLang(s) {
    return s === "sv" || s === "en";
}
export function langOfLocale(locale) {
    if (typeof locale !== "string")
        return null;
    const l = locale.trim().slice(0, 2).toLowerCase();
    return isCaptionLang(l) ? l : null;
}
export function otherCaptionLang(l) {
    return l === "sv" ? "en" : "sv";
}
export const CUE_LINE_CHARS = 42;
export const CUE_MAX_CHARS = CUE_LINE_CHARS * 2;
export const CUE_MAX_SECONDS = 7;
export const CUE_MIN_SECONDS = 1;
const clean = (s) => s.replace(/\s+/g, " ").trim();
const ENDS_CLAUSE = /[.!?…,;:]["'»”)]*$/;
export function cuesFromPhrases(phrases) {
    var _a;
    const out = [];
    for (const p of phrases) {
        const text = clean(p.text);
        if (!text || !(p.end > p.start))
            continue;
        if (text.length <= CUE_MAX_CHARS && p.end - p.start <= CUE_MAX_SECONDS) {
            out.push({ start: p.start, end: p.end, text });
            continue;
        }
        const words = ((_a = p.words) !== null && _a !== void 0 ? _a : []).filter((w) => clean(w.text));
        if (words.length > 0)
            out.push(...splitByWords(words));
        else
            out.push(...splitByLength(text, p.start, p.end));
    }
    return tidyCues(out);
}
function splitByWords(words) {
    const out = [];
    let cur = [];
    const flush = () => {
        if (cur.length === 0)
            return;
        out.push({
            start: cur[0].start,
            end: cur[cur.length - 1].end,
            text: clean(cur.map((w) => w.text).join(" ")),
        });
        cur = [];
    };
    for (const w of words) {
        if (cur.length > 0) {
            const text = clean([...cur, w].map((x) => x.text).join(" "));
            const tooLong = text.length > CUE_MAX_CHARS;
            const tooSlow = w.end - cur[0].start > CUE_MAX_SECONDS;
            if (tooLong || tooSlow)
                flush();
        }
        cur.push(w);
        const sofar = clean(cur.map((x) => x.text).join(" "));
        if (ENDS_CLAUSE.test(w.text) && sofar.length >= CUE_LINE_CHARS)
            flush();
    }
    flush();
    return out;
}
function splitByLength(text, start, end) {
    const chunks = [];
    let cur = "";
    for (const word of text.split(" ")) {
        const next = cur ? `${cur} ${word}` : word;
        if (next.length > CUE_MAX_CHARS && cur) {
            chunks.push(cur);
            cur = word;
        }
        else
            cur = next;
    }
    if (cur)
        chunks.push(cur);
    const total = chunks.reduce((n, c) => n + c.length, 0) || 1;
    const out = [];
    let t = start;
    for (const c of chunks) {
        const d = ((end - start) * c.length) / total;
        out.push({ start: t, end: t + d, text: c });
        t += d;
    }
    return out;
}
const ms = (n) => Math.round(n * 1000) / 1000;
export function tidyCues(cues) {
    const sorted = cues
        .filter((c) => c.text && Number.isFinite(c.start) && Number.isFinite(c.end))
        .map((c) => ({ start: Math.max(0, c.start), end: c.end, text: c.text }))
        .sort((a, b) => a.start - b.start);
    for (let i = 0; i < sorted.length; i++) {
        const c = sorted[i];
        const next = sorted[i + 1];
        const limit = next ? next.start : Number.POSITIVE_INFINITY;
        if (c.end > limit)
            c.end = limit;
        if (c.end - c.start < CUE_MIN_SECONDS)
            c.end = Math.min(c.start + CUE_MIN_SECONDS, limit);
    }
    return sorted
        .filter((c) => c.end > c.start)
        .map((c) => ({ start: ms(c.start), end: ms(c.end), text: c.text }));
}
export function wrapCueText(text, width = CUE_LINE_CHARS) {
    const t = clean(text);
    if (t.length <= width)
        return [t];
    const mid = t.length / 2;
    let best = -1;
    for (let i = 0; i < t.length; i++) {
        if (t[i] === " " && (best < 0 || Math.abs(i - mid) < Math.abs(best - mid)))
            best = i;
    }
    return best < 0 ? [t] : [t.slice(0, best), t.slice(best + 1)];
}
export function vttTimestamp(seconds) {
    const total = Math.max(0, Math.round(seconds * 1000));
    const h = Math.floor(total / 3600000);
    const m = Math.floor((total % 3600000) / 60000);
    const s = Math.floor((total % 60000) / 1000);
    const msPart = total % 1000;
    const two = (n) => (n < 10 ? `0${n}` : `${n}`);
    const three = (n) => (n < 10 ? `00${n}` : n < 100 ? `0${n}` : `${n}`);
    return `${two(h)}:${two(m)}:${two(s)}.${three(msPart)}`;
}
function escapeVtt(s) {
    return (s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;"));
}
export function toWebVtt(cues) {
    const blocks = cues.map((c, i) => `${i + 1}\n${vttTimestamp(c.start)} --> ${vttTimestamp(c.end)}\n` +
        wrapCueText(c.text).map(escapeVtt).join("\n"));
    return `WEBVTT\n\n${blocks.join("\n\n")}\n`;
}
const VTT_TIME = /(?:(\d+):)?(\d{1,2}):(\d{2})[.,](\d{3})/;
function parseVttTime(s) {
    const m = VTT_TIME.exec(s);
    if (!m)
        return null;
    return ((m[1] ? Number(m[1]) * 3600 : 0) +
        Number(m[2]) * 60 +
        Number(m[3]) +
        Number(m[4]) / 1000);
}
function unescapeVtt(s) {
    return s
        .replace(/<[^>]*>/g, "")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&");
}
export function parseWebVtt(text) {
    const out = [];
    const blocks = text.replace(/\r\n?/g, "\n").split(/\n{2,}/);
    for (const block of blocks) {
        const lines = block.split("\n");
        const at = lines.findIndex((l) => l.includes("-->"));
        if (at < 0)
            continue;
        const [a, b] = lines[at].split("-->");
        const start = parseVttTime(a);
        const end = parseVttTime(b !== null && b !== void 0 ? b : "");
        const body = clean(unescapeVtt(lines.slice(at + 1).join(" ")));
        if (start === null || end === null || !body || !(end > start))
            continue;
        out.push({ start, end, text: body });
    }
    return out.sort((x, y) => x.start - y.start);
}
export const MAX_CUES = 8000;
export const MAX_CUE_CHARS = 300;
export function sanitiseCues(raw) {
    if (!Array.isArray(raw) || raw.length > MAX_CUES)
        return null;
    const out = [];
    for (const c of raw) {
        if (!c || typeof c !== "object")
            return null;
        const { start, end, text } = c;
        if (typeof start !== "number" ||
            typeof end !== "number" ||
            !Number.isFinite(start) ||
            !Number.isFinite(end) ||
            start < 0 ||
            end <= start ||
            typeof text !== "string")
            return null;
        const t = clean(text);
        if (!t || t.length > MAX_CUE_CHARS)
            return null;
        out.push({ start: ms(start), end: ms(end), text: t });
    }
    return out.sort((a, b) => a.start - b.start);
}
export function sameCues(a, b) {
    if (a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i].text !== b[i].text ||
            Math.abs(a[i].start - b[i].start) > 0.0005 ||
            Math.abs(a[i].end - b[i].end) > 0.0005)
            return false;
    }
    return true;
}
export function cueIndexAt(cues, t) {
    let lo = 0;
    let hi = cues.length - 1;
    let hit = -1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (cues[mid].start <= t) {
            hit = mid;
            lo = mid + 1;
        }
        else
            hi = mid - 1;
    }
    return hit >= 0 && t < cues[hit].end + 0.75 ? hit : -1;
}
